/**
 * Voice AI — Calls Table
 * Renders call history table with sortable columns, expandable rows
 * (AI summary, sentiment, transcript, tool calls), and pagination.
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { callsStyles } from '../../styles/calls-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { callsService } from '../../services/calls-service.js';
import { tz } from '../../services/timezone-service.js';

// Topic tag color rotation
const TOPIC_COLORS = ['green', 'blue', 'amber', 'purple'];

export class CallsTable extends LitElement {
  static properties = {
    calls:         { type: Array },
    loading:       { type: Boolean },
    sortBy:        { type: String },
    sortDirection: { type: String },
    currentPage:   { type: Number },
    totalCalls:    { type: Number },
    pageSize:      { type: Number },
    expandedRowId: { type: String, state: true },
    _detailData:   { type: Object, state: true },
    _detailLoading:{ type: Boolean, state: true },
    _showTranscript: { type: Boolean, state: true },
  };

  static styles = [designSystem, componentStyles, callsStyles];

  constructor() {
    super();
    this.calls = [];
    this.loading = false;
    this.sortBy = 'dateTime';
    this.sortDirection = 'desc';
    this.currentPage = 1;
    this.totalCalls = 0;
    this.pageSize = 20;
    this.expandedRowId = null;
    this._detailData = null;
    this._detailLoading = false;
    this._showTranscript = false;
  }

  // ── Handlers ──

  _handleSort(column) {
    this._emit('sort', { column });
  }

  _goToPage(p) {
    const max = Math.max(1, Math.ceil(this.totalCalls / this.pageSize));
    const page = Math.max(1, Math.min(p, max));
    this._emit('page-change', { page });
  }

  async _toggleRow(call) {
    if (this.expandedRowId === call.id) {
      this.expandedRowId = null;
      this._detailData = null;
      this._showTranscript = false;
      return;
    }

    this.expandedRowId = call.id;
    this._detailData = null;
    this._detailLoading = true;
    this._showTranscript = false;

    try {
      const data = await callsService.getCallDetail(call.dbId);
      if (data.success) {
        this._detailData = data;
      }
    } catch (err) {
      console.error('[CallsTable] Failed to load detail', err);
    } finally {
      this._detailLoading = false;
    }
  }

  _toggleTranscript() {
    this._showTranscript = !this._showTranscript;
  }

  _formatTimestampMs(epochMs) {
    if (!epochMs) return '--:--:--.---';
    const d = new Date(epochMs);
    const time = d.toLocaleTimeString('en-US', {
      timeZone: tz.tz, hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
    const ms = d.getMilliseconds().toString().padStart(3, '0');
    return `${time}.${ms}`;
  }

  /** Format elapsed time from a reference epoch ms. Returns m:ss */
  _formatElapsed(epochMs, refEpochMs) {
    if (!epochMs || !refEpochMs) return '';
    const elapsed = epochMs - refEpochMs;
    if (elapsed < 0) return '0:00';
    const totalSec = Math.floor(elapsed / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  _emit(name, detail) {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  // ── Computed ──

  get _totalPages() {
    return Math.max(1, Math.ceil(this.totalCalls / this.pageSize));
  }

  // ── Sentiment helpers ──

  _getSentimentClass(call) {
    // Future: use call.sentiment when available
    if (call.sentiment === 'positive') return 'positive';
    if (call.sentiment === 'negative') return 'negative';
    if (call.sentiment === 'neutral') return 'neutral';
    return 'unknown';
  }

  _getSentimentWidth(call) {
    // Future: use call.sentimentScore when available
    if (call.sentimentScore != null) return call.sentimentScore;
    // Placeholder: vary based on status for visual interest
    if (call.status === 'completed') return 72;
    if (call.status === 'failed') return 28;
    return 50;
  }

  // ── Render ──

  render() {
    const startIdx = (this.currentPage - 1) * this.pageSize;
    const endIdx = Math.min(startIdx + this.calls.length, this.totalCalls);

    return html`
      ${iconStyles}
      <div class="table-card">
        <div class="table-scroll">
          <table>
            <thead>
              <tr>
                ${this._renderTh('Date / Time', 'dateTime')}
                ${this._renderTh('Phone Number', 'phone')}
                ${this._renderTh('Agent', 'agent')}
                ${this._renderTh('Duration', 'duration')}
                <th>Direction</th>
                ${this._renderTh('Status', 'status')}
                <th>Sentiment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              ${this.loading
                ? [1,2,3,4,5].map(() => html`
                  <tr class="sk-row">
                    <td>
                      <div style="display:flex;flex-direction:column;gap:4px;">
                        <div class="skeleton-line" style="width:80px;"></div>
                        <div class="skeleton-line xs" style="width:50px;"></div>
                      </div>
                    </td>
                    <td><div class="skeleton-line" style="width:110px;"></div></td>
                    <td>
                      <div style="display:flex;align-items:center;gap:8px;">
                        <div class="skeleton-line" style="width:28px;height:28px;border-radius:50%;flex-shrink:0;"></div>
                        <div class="skeleton-line" style="width:80px;"></div>
                      </div>
                    </td>
                    <td><div class="skeleton-line" style="width:50px;"></div></td>
                    <td><div class="skeleton-line" style="width:36px;"></div></td>
                    <td><div class="skeleton-line" style="width:70px;height:22px;border-radius:10px;"></div></td>
                    <td><div class="skeleton-line" style="width:60px;height:6px;border-radius:3px;"></div></td>
                    <td>
                      <div style="display:flex;gap:4px;">
                        <div class="skeleton-line" style="width:28px;height:28px;border-radius:6px;"></div>
                        <div class="skeleton-line" style="width:28px;height:28px;border-radius:6px;"></div>
                        <div class="skeleton-line" style="width:28px;height:28px;border-radius:6px;"></div>
                      </div>
                    </td>
                  </tr>
                `)
                : this.calls.length > 0
                  ? this.calls.map(call => this._renderRow(call))
                  : html`
                    <tr class="empty-row">
                      <td colspan="8">
                        <div class="empty-content">
                          <i class="fa-sharp fa-light fa-rectangle-history-circle-plus empty-icon"></i>
                          <div class="empty-title">No calls found</div>
                          <div class="empty-desc">Try adjusting your search or filter criteria.</div>
                        </div>
                      </td>
                    </tr>
                  `
              }
            </tbody>
          </table>
        </div>

        ${this.totalCalls > 0 ? html`
          <div class="pagination-bar">
            <div class="pagination-info">
              Showing <strong>${startIdx + 1}-${endIdx}</strong> of <strong>${this.totalCalls.toLocaleString()}</strong>
            </div>
            <div class="pagination-controls">
              <button class="page-btn" ?disabled="${this.currentPage <= 1}" @click="${() => this._goToPage(this.currentPage - 1)}">
                <i class="fa-sharp fa-light fa-chevron-left"></i>
              </button>
              ${this._renderPageNumbers()}
              <button class="page-btn" ?disabled="${this.currentPage >= this._totalPages}" @click="${() => this._goToPage(this.currentPage + 1)}">
                <i class="fa-sharp fa-light fa-chevron-right"></i>
              </button>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }

  _renderTh(label, column) {
    const isActive = this.sortBy === column;
    return html`
      <th class="sortable" @click="${() => this._handleSort(column)}">
        ${label}
        <span class="sort-arrows">
          <span class="up ${isActive && this.sortDirection === 'asc' ? 'active' : ''}">&#9650;</span>
          <span class="down ${isActive && this.sortDirection === 'desc' ? 'active' : ''}">&#9660;</span>
        </span>
      </th>
    `;
  }

  _renderRow(call) {
    const isExpanded = this.expandedRowId === call.id;
    const sentimentClass = this._getSentimentClass(call);
    const sentimentWidth = this._getSentimentWidth(call);

    return html`
      <tr
        class="${isExpanded ? 'expanded-parent' : ''}"
        @click="${() => this._toggleRow(call)}"
      >
        <td>
          <div class="cell-datetime">
            <span class="date">${callsService.formatDate(call.dateTime)}</span>
            <span class="time">${callsService.formatTime(call.dateTime)}</span>
          </div>
        </td>
        <td>
          <span class="cell-phone">
            <i class="fa-sharp fa-light ${call.phone === 'Testing' || call.phone === 'testing' ? 'fa-headset' : 'fa-phone'} cell-phone-icon"></i>
            ${callsService.formatPhone(call.phone)}
          </span>
        </td>
        <td>
          <div class="cell-agent">
            <span class="agent-avatar"><i class="fa-sharp fa-light fa-robot"></i></span>
            ${call.agent}
          </div>
        </td>
        <td><span class="cell-duration">${callsService.formatDuration(call.duration)}</span></td>
        <td>
          <span class="cell-direction ${call.direction}">
            <i class="fa-sharp fa-light ${call.direction === 'inbound' ? 'fa-arrow-down-left' : 'fa-arrow-up-right'}"></i>
            ${call.direction === 'inbound' ? 'In' : 'Out'}
          </span>
        </td>
        <td><span class="badge ${callsService.statusBadgeClass(call.status)}">${callsService.statusDisplayLabel(call.status)}</span></td>
        <td>
          <div class="cell-sentiment">
            <div class="sentiment-bar">
              <div class="sentiment-fill ${sentimentClass}" style="width: ${sentimentWidth}%"></div>
            </div>
          </div>
        </td>
        <td>
          <div class="cell-actions">
            <button class="action-btn" title="Recording not available yet" disabled @click="${(e) => e.stopPropagation()}">
              <i class="fa-sharp fa-light fa-play"></i>
            </button>
            <button class="action-btn ${isExpanded ? 'active' : ''}" title="View transcript" @click="${(e) => { e.stopPropagation(); this._toggleRow(call); }}">
              <i class="fa-sharp fa-light fa-message-lines"></i>
            </button>
            <button class="action-btn" title="Download not available yet" disabled @click="${(e) => e.stopPropagation()}">
              <i class="fa-sharp fa-light fa-download"></i>
            </button>
          </div>
        </td>
      </tr>
      ${isExpanded ? this._renderExpandedRow(call) : ''}
    `;
  }

  _renderExpandedRow(call) {
    if (this._detailLoading) {
      return html`
        <tr class="expanded-detail">
          <td colspan="8">
            <div class="detail-loading">
              <div class="loading-spinner"></div>
              Loading call details...
            </div>
          </td>
        </tr>
      `;
    }

    const detail = this._detailData;
    const transcripts = detail?.transcripts || [];
    const toolCalls = detail?.toolCalls || [];
    const callData = detail?.call || {};

    // Extract tool names as pseudo-topics when no real topics available
    const topics = callData.topics || [];
    const pseudoTopics = topics.length > 0
      ? topics
      : (toolCalls.length > 0
        ? [...new Set(toolCalls.map(tc => tc.toolName || tc.tool_name))].map(name =>
            name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
          )
        : []);

    // AI Summary placeholder — use first transcript as preview if available
    const summaryText = callData.summary || null;
    const previewText = !summaryText && transcripts.length > 0
      ? transcripts.slice(0, 2).map(t => `${t.speaker}: ${t.content}`).join(' — ')
      : null;

    // Sentiment score
    const sentimentScore = callData.sentimentScore ?? null;
    const sentimentClass = this._getSentimentClass(call);

    return html`
      <tr class="expanded-detail">
        <td colspan="8">
          <div class="detail-inner">
            <!-- Left: AI Summary + Key Topics -->
            <div class="detail-section">
              <div class="detail-section-title">
                <i class="fa-sharp fa-light fa-brain"></i>
                AI Summary
              </div>
              ${summaryText
                ? html`<div class="ai-summary-text">${summaryText}</div>`
                : previewText
                  ? html`<div class="ai-summary-text">${previewText}</div>`
                  : html`<div class="ai-summary-text placeholder">AI summary will be available when sentiment analysis is enabled.</div>`
              }

              <div class="detail-section-title" style="margin-top: var(--space-4);">
                <i class="fa-sharp fa-light fa-tags"></i>
                Key Topics
              </div>
              ${pseudoTopics.length > 0
                ? html`
                  <div class="topics-list">
                    ${pseudoTopics.map((topic, i) => html`
                      <span class="topic-tag ${TOPIC_COLORS[i % TOPIC_COLORS.length]}">${topic}</span>
                    `)}
                  </div>
                `
                : html`<span class="no-data">Topics will appear here when enabled.</span>`
              }
            </div>

            <!-- Right: Sentiment Score + View Transcript -->
            <div class="detail-section">
              <div class="detail-section-title">
                <i class="fa-sharp fa-light fa-face-smile"></i>
                Sentiment Score
              </div>
              <div class="sentiment-score-section">
                <div class="sentiment-score-display">
                  <span class="sentiment-score-label">
                    ${sentimentScore != null ? `${sentimentScore}%` : 'N/A'}
                  </span>
                  <div class="sentiment-score-bar">
                    <div class="sentiment-score-fill ${sentimentClass}" style="width: ${sentimentScore ?? 50}%"></div>
                  </div>
                </div>
              </div>

              <button class="view-transcript-btn" @click="${() => this._toggleTranscript()}">
                <i class="fa-sharp fa-light ${this._showTranscript ? 'fa-eye-slash' : 'fa-eye'}"></i>
                ${this._showTranscript ? 'Hide Full Transcript' : 'View Full Transcript'}
              </button>

              <!-- Call Details -->
              <div class="detail-section-title" style="margin-top: var(--space-4);">
                <i class="fa-sharp fa-light fa-circle-info"></i>
                Call Details
              </div>
              <div class="call-meta">
                <div class="meta-item">
                  <span class="meta-label">Call SID</span>
                  <span class="meta-value">${call.callSid || '--'}</span>
                </div>
                <div class="meta-item">
                  <span class="meta-label">Duration</span>
                  <span class="meta-value">${callsService.formatDuration(call.duration)}</span>
                </div>
                ${callData.customerId ? html`
                  <div class="meta-item">
                    <span class="meta-label">Customer ID</span>
                    <span class="meta-value">${callData.customerId}</span>
                  </div>
                ` : ''}
                ${callData.bookingId ? html`
                  <div class="meta-item">
                    <span class="meta-label">Booking</span>
                    <span class="meta-value">#${callData.bookingId}</span>
                  </div>
                ` : ''}
                ${callData.costUsd ? html`
                  <div class="meta-item">
                    <span class="meta-label">Cost</span>
                    <span class="meta-value">$${callData.costUsd.toFixed(4)}</span>
                  </div>
                ` : ''}
              </div>
            </div>

            <!-- Full Transcript + Tool Calls (toggleable) -->
            ${this._showTranscript ? html`
              <div class="full-transcript-section">
                <div class="detail-section">
                  <div class="detail-section-title">
                    <i class="fa-sharp fa-light fa-message-lines"></i>
                    Transcript ${transcripts.length > 0 ? html`<span style="font-weight:normal;color:var(--color-text-tertiary)">(${transcripts.length} messages)</span>` : ''}
                  </div>
                  ${transcripts.length > 0 ? html`
                    <div class="transcript-list">
                      ${transcripts.map(t => html`
                        <div class="transcript-msg">
                          <span class="msg-timestamp">${this._formatTimestampMs(t.timestampMs)}</span>
                          <span class="speaker-label ${t.speaker}">${t.speaker === 'agent' ? 'AI' : 'Caller'}</span>
                          <span class="msg-content">${t.content}</span>
                        </div>
                      `)}
                    </div>
                  ` : html`<span class="no-data">No transcript available for this call.</span>`}
                </div>

                ${toolCalls.length > 0 ? html`
                  <div class="detail-section" style="margin-top: var(--space-4);">
                    <div class="detail-section-title">
                      <i class="fa-sharp fa-light fa-wrench"></i>
                      AI Actions <span style="font-weight:normal;color:var(--color-text-tertiary)">(${toolCalls.length})</span>
                    </div>
                    <div class="tool-list">
                      ${toolCalls.map(tc => {
                        const tcMs = tc.executedAt ? new Date(tc.executedAt).getTime() : 0;
                        const refMs = transcripts.length > 0 ? transcripts[0].timestampMs : 0;
                        return html`
                        <div class="tool-item ${tc.status === 'error' ? 'has-error' : ''}">
                          <div class="tool-item-row">
                            ${tcMs && refMs ? html`<span class="tool-timestamp">${this._formatElapsed(tcMs, refMs)}</span>` : ''}
                            <span class="tool-name">${tc.toolName || tc.tool_name}</span>
                            ${tc.executionTimeMs ? html`<span class="tool-time">${tc.executionTimeMs}ms</span>` : ''}
                            <span class="tool-status ${tc.status}">${tc.status}</span>
                          </div>
                          ${tc.status === 'error' && tc.errorMessage ? html`
                            <div class="tool-error">${tc.errorMessage}</div>
                          ` : ''}
                        </div>
                      `;})}
                    </div>
                  </div>
                ` : ''}
              </div>
            ` : ''}
          </div>
        </td>
      </tr>
    `;
  }

  _renderPageNumbers() {
    const total = this._totalPages;
    const current = this.currentPage;
    const pages = [];
    const range = 2;

    pages.push(1);
    if (current - range > 2) pages.push('...');
    for (let i = Math.max(2, current - range); i <= Math.min(total - 1, current + range); i++) {
      pages.push(i);
    }
    if (current + range < total - 1) pages.push('...');
    if (total > 1) pages.push(total);

    return pages.map(p =>
      p === '...'
        ? html`<span class="page-ellipsis">...</span>`
        : html`
          <button
            class="page-btn ${p === current ? 'active' : ''}"
            @click="${() => this._goToPage(p)}"
          >${p}</button>
        `
    );
  }
}

customElements.define('calls-table', CallsTable);
